/*
 * Adaptive HLS video helper.
 *
 * Streams Shopify-hosted videos over HLS so browsers only download the quality
 * (and the portion) they actually need, instead of pulling a full fixed-bitrate
 * MP4. Uses native HLS on Safari/iOS, lazy-loads hls.js elsewhere, and falls
 * back to an MP4 when HLS is unavailable.
 *
 * Public API (window.HLSVideo):
 *   set(video, hlsSrc, mp4Src, opts)  (re)configure a <video> imperatively
 *   clear(video)                      tear down and reset a <video>
 *   initAll(root)                     wire up any <video data-hls-src|data-mp4-src>
 *
 * Declarative usage: add data-hls-src / data-mp4-src (and optional
 * data-hls-lazy="true") to a <video>; it is initialised automatically.
 */
(function () {
  'use strict';

  if (window.HLSVideo) return;

  var HLS_CDN = 'https://cdn.jsdelivr.net/npm/hls.js@1/dist/hls.min.js';
  var libPromise = null;

  function loadLib() {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (libPromise) return libPromise;
    libPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = HLS_CDN;
      s.async = true;
      s.onload = function () { resolve(window.Hls); };
      s.onerror = function () { reject(new Error('hls.js failed to load')); };
      document.head.appendChild(s);
    });
    return libPromise;
  }

  function nativeHls(video) {
    return !!video.canPlayType && video.canPlayType('application/vnd.apple.mpegurl') !== '';
  }

  function tryPlay(video) {
    if (!video.autoplay && !video.__forcePlay) return;
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  function useMp4(video, mp4Src) {
    if (!mp4Src) return;
    video.src = mp4Src;
    video.load();
    tryPlay(video);
  }

  function clear(video) {
    if (!video) return;
    if (video.__hls) {
      try { video.__hls.destroy(); } catch (e) {}
      video.__hls = null;
    }
    try { video.pause(); } catch (e) {}
    while (video.firstChild) video.removeChild(video.firstChild);
    video.removeAttribute('src');
    try { video.load(); } catch (e) {}
  }

  function set(video, hlsSrc, mp4Src, opts) {
    if (!video) return;
    opts = opts || {};
    clear(video);
    video.__forcePlay = !!opts.autoplay;

    // Safari / iOS play HLS natively and honour preload="metadata".
    if (hlsSrc && nativeHls(video)) {
      video.src = hlsSrc;
      video.load();
      tryPlay(video);
      return;
    }

    // Everyone else streams via hls.js (loaded on demand).
    if (hlsSrc) {
      loadLib().then(function (Hls) {
        // Element may have been reconfigured/cleared while the lib loaded.
        if (video.__forcePlay === undefined) return;
        if (Hls && Hls.isSupported()) {
          var hls = new Hls({ capLevelToPlayerSize: true });
          video.__hls = hls;
          hls.attachMedia(video);
          hls.on(Hls.Events.MEDIA_ATTACHED, function () { hls.loadSource(hlsSrc); });
          hls.on(Hls.Events.MANIFEST_PARSED, function () { tryPlay(video); });
          hls.on(Hls.Events.ERROR, function (evt, data) {
            if (data && data.fatal) {
              try { hls.destroy(); } catch (e) {}
              video.__hls = null;
              useMp4(video, mp4Src);
            }
          });
        } else {
          useMp4(video, mp4Src);
        }
      }).catch(function () {
        useMp4(video, mp4Src);
      });
      return;
    }

    useMp4(video, mp4Src);
  }

  function initEl(video) {
    if (video.__hlsInit) return;
    video.__hlsInit = true;
    set(video, video.getAttribute('data-hls-src') || '', video.getAttribute('data-mp4-src') || '', {
      autoplay: video.hasAttribute('autoplay')
    });
  }

  function observeLazy(video) {
    if (!('IntersectionObserver' in window)) { initEl(video); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          io.unobserve(entry.target);
          initEl(entry.target);
        }
      });
    }, { rootMargin: '200px 0px', threshold: 0.1 });
    io.observe(video);
  }

  function initAll(root) {
    var vids = (root || document).querySelectorAll('video[data-hls-src], video[data-mp4-src]');
    for (var i = 0; i < vids.length; i++) {
      var v = vids[i];
      if (v.__hlsInit) continue;
      if (v.getAttribute('data-hls-lazy') === 'true') observeLazy(v);
      else initEl(v);
    }
  }

  window.HLSVideo = { loadLib: loadLib, set: set, clear: clear, init: initEl, initAll: initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
})();
